import React from 'react'
import { NavLink } from 'react-router-dom'
import { MenuItem, Box } from '@mui/material'
import { IconSidebar, MenuItemSidebar } from './styled'
import { ReactComponent as SupportIcon } from '@assets/images/icons/support.svg'
import storage from '@/utils/storage'

interface SidebarItemProps {
  route: {
    path: string
    sidebarName: string
    iconLight?: string
    iconDark?: string
    target?: string
  }
  isActive: boolean
  isHovered?: boolean
  isMobile?: boolean
  onItemClick?: () => void
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  route,
  isActive,
  isHovered = false,
  isMobile = false,
  onItemClick,
}) => {
  const supportEmail = process.env.SUPPORT_EMAIL

  const handleClick = (e: React.MouseEvent) => {
    if (route.path === '/contact-support') {
      e.preventDefault()
      window.location.href = `mailto:${supportEmail}`
    }
    onItemClick?.()
  }

  const renderIcon = () => {
    const iconSize = isMobile ? '20' : '24'
    const iconStyle = isMobile
      ? {
          width: '20px',
          height: '20px',
          objectFit: 'contain' as const,
          maxWidth: '20px',
          maxHeight: '20px',
        }
      : undefined

    if (route.path === '/contact-support') {
      const iconContainer = (
        <SupportIcon
          width={iconSize}
          height={iconSize}
          style={{ color: '#9b9b9b' }}
        />
      )

      return isMobile ? (
        iconContainer
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          {iconContainer}
        </div>
      )
    }

    return isMobile ? (
      <img
        src={isActive ? route.iconDark : route.iconLight}
        alt={route.sidebarName}
        style={iconStyle}
      />
    ) : (
      <IconSidebar src={isActive ? route.iconDark : route.iconLight} />
    )
  }

  const getMenuItemStyles = () => {
    const baseStyles = {
      display: 'flex',
      width: '100%',
      '&.Mui-selected': {
        backgroundColor: '#F3F3F3',
        borderLeft: '5px solid #000000',
        transition: 'all .2s',
      },
    }

    if (isMobile) {
      return {
        ...baseStyles,
        justifyContent: 'flex-start',
        paddingLeft: '10px',
        '&.Mui-selected': {
          backgroundColor: '#F3F3F3',
          borderLeft: '5px solid #000000',
        },
      }
    }

    return {
      ...baseStyles,
      justifyContent: 'flex-start',
    }
  }

  const getIconBoxStyles = () => {
    if (isMobile) {
      return {
        width: '30px',
        height: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '30px',
        minHeight: '30px',
      }
    }

    return {
      width: '30px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }
  }

  const menuItemContent = (
    <MenuItem
      selected={isActive}
      onClick={route.path === '/contact-support' ? handleClick : undefined}
      sx={getMenuItemStyles()}
    >
      <Box sx={getIconBoxStyles()}>{renderIcon()}</Box>
      {(isHovered || isMobile) && (
        <Box
          sx={
            isMobile
              ? {
                  marginLeft: '12px',
                  color: isActive ? '#000000' : '#9b9b9b',
                  fontWeight: 600,
                  fontSize: '16px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflow: 'hidden',
                  flex: 1,
                }
              : {}
          }
        >
          {isMobile ? (
            route.sidebarName
          ) : (
            <MenuItemSidebar selected={isActive} style={{ marginLeft: '12px' }}>
              {route.sidebarName}
            </MenuItemSidebar>
          )}
        </Box>
      )}
    </MenuItem>
  )

  const isContactSupport = route.path === '/contact-support'

  return isContactSupport ? (
    <div style={{ textDecoration: 'none' }}>{menuItemContent}</div>
  ) : (
    <NavLink
      to={route.path}
      style={{ textDecoration: 'none' }}
      target={route?.target || ''}
      onClick={handleClick}
    >
      {menuItemContent}
    </NavLink>
  )
}

export default SidebarItem
